import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import i18n from 'i18next';

import { handleRefreshToken } from '@store/features/auth/token/actions';
import { loadingOff, loadingOn } from '@store/features/loading/slice';
import store from '@store/store';
import handleLogout from '@utils/handleLogout';
import handleToast from '@utils/handleToast';
import { getAuthTokenFromLocalStorage, isExpireToken } from '@utils/token';

import { apiUrl, authUrl } from './urls';

const { t } = i18n;

const axiosInstance: AxiosInstance = axios.create({
  baseURL: apiUrl,
});

const tokenInstance: AxiosInstance = axios.create({
  baseURL: authUrl,
});

const addParams = (url: string, params: Record<string, string | number> = {}) => {
  if (Object.keys(params).length) {
    return `${url}?${Object.keys(params)
      .map(key => `${key}=${params[key]}`)
      .join('&')}`;
  }
  return url;
};

export const get = <R = Record<string, unknown>>(
  url: string,
  payload: any = {},
): Promise<AxiosResponse<R>> => axiosInstance.get(addParams(url, payload));

export const post = <R = Record<string, unknown>>(
  url: string,
  payload: any = {},
): Promise<AxiosResponse<R>> => axiosInstance.post(url, payload);

export const postFormWithFiles = <R = Record<string, unknown>>(
  url: string,
  payload: any = {},
): Promise<AxiosResponse<R>> => {
  const formData = new FormData();
  if (payload.images && payload.images.length) {
    payload.images.forEach((file: File) => {
      formData.append('images', file);
    });
  }
  Object.keys(payload)
    .filter(fieldName => !!payload[fieldName] && fieldName !== 'images')
    .forEach(fieldName => {
      formData.append(fieldName, payload[fieldName]);
    });
  return axiosInstance.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const put = <R = Record<string, unknown>>(
  url: string,
  payload: any = {},
): Promise<AxiosResponse<R>> => axiosInstance.put(url, payload);

export const putFormWithFiles = <R = Record<string, unknown>>(
  url: string,
  payload: any = {},
): Promise<AxiosResponse<R>> => {
  const formData = new FormData();
  if (payload.images && payload.images.length) {
    payload.images.forEach((file: File) => {
      formData.append('images', file);
    });
  }
  Object.keys(payload)
    .filter(fieldName => !!payload[fieldName] && fieldName !== 'images')
    .forEach(fieldName => {
      formData.append(fieldName, payload[fieldName]);
    });
  return axiosInstance.put(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const deletes = <R = Record<string, unknown>>(
  url: string,
  payload: any = {},
): Promise<AxiosResponse<R>> => axiosInstance.delete(url, { data: payload });

export const upload = <R = Record<string, unknown>>(
  url: string,
  file: File,
): Promise<AxiosResponse<R>> => {
  const formData = new FormData();
  formData.append('formFile', file);

  return axiosInstance.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const postToken = <R = Record<string, unknown>>(
  url: string,
  payload: any = {},
): Promise<AxiosResponse<R>> => tokenInstance.post(url, payload);

axiosInstance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  store.dispatch(loadingOn());

  const token = getAuthTokenFromLocalStorage();

  if (token) {
    const isExpire = isExpireToken(token);

    if (isExpire) {
      await store.dispatch(handleRefreshToken());

      config.headers.Authorization = `Bearer ${getAuthTokenFromLocalStorage()}`;

      return config;
    }

    config.headers.Authorization = `Bearer ${token}`;

    return config;
  }

  return config;
});

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    store.dispatch(loadingOff());

    return response;
  },
  (reject: any) => {
    console.log(reject);

    if (reject.response) {
      const { data, status } = reject.response;

      if (status === 401) {
        handleLogout();
      } else if (data?.errors && Object.keys(data.errors).length) {
        Object.keys(data.errors).forEach((key: string) => {
          data.errors[key].forEach((error: string): void => {
            handleToast('error', error);
          });
        });
      } else if (data.detail) {
        handleToast('error', data.detail);
      } else if (data.title) {
        handleToast('error', data.title);
      } else {
        handleToast('error', t('common.messages.somethingWentWrong'));
      }
    } else {
      handleToast('error', t('common.messages.somethingWentWrong'));
    }

    store.dispatch(loadingOff());

    return Promise.reject(reject);
  },
);
